/**
 * Notification React Query Hooks
 *
 * Provides data fetching and mutation hooks for notification-related operations.
 */

import type { NotificationListParams } from '@/shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/notification-service';

/**
 * Query key factory for notification queries
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params?: NotificationListParams) => [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const
};

/**
 * Hook to fetch paginated list of notifications
 *
 * @param params - Optional filters (is_read, notification_type) and pagination
 * @returns Query result with paginated notifications
 */
export function useNotifications(params?: NotificationListParams) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationService.list(params),
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Hook to fetch unread notification count for bell badge
 *
 * Uses a short staleTime (30s) for near-realtime badge updates.
 *
 * @returns Query result with { count: number }
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000
  });
}

/**
 * Hook to mark a single notification as read
 *
 * @returns Mutation that accepts a notification ID
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}

/**
 * Hook to mark all notifications as read
 *
 * @returns Mutation that marks all unread notifications as read
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}

/**
 * Hook to permanently dismiss a notification
 *
 * @returns Mutation that accepts a notification ID
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });
}
